
import { supabase } from '@/lib/supabase';
import { analyzeFeedback } from './ai-feedback-analysis-service';
import OpenAI from 'openai';

export interface RuleImprovementSuggestion {
  rule_id: number;
  pattern: string;
  current_response: string;
  negative_feedback_count: number;
  total_usage: number;
  negative_ratio: number;
  suggested_improvement: string;
  confidence_score: number;
  status: 'pending' | 'approved' | 'rejected' | 'implemented';
}

// Analyze rules with high negative feedback rates and generate improvement suggestions
export const generateRuleImprovementSuggestions = async (
  startDate: Date,
  endDate: Date,
  minNegativeRatio = 20,
  minUsageCount = 5
): Promise<RuleImprovementSuggestion[]> => {
  try {
    // Get feedback analysis for the time period
    const analysis = await analyzeFeedback(startDate, endDate);
    
    // Filter rules with significant negative feedback
    const problematicRules = (analysis.ruleImprovementSuggestions || [])
      .filter(rule => rule.negative_ratio >= minNegativeRatio && rule.total_usage >= minUsageCount);
    
    // For each rule, fetch additional data and generate improvements
    const improvementSuggestions: RuleImprovementSuggestion[] = await Promise.all(
      problematicRules.map(async (rule) => {
        // Get the current rule response
        const { data: ruleData, error: ruleError } = await supabase
          .from('assistant_rules')
          .select('response')
          .eq('id', rule.rule_id)
          .single();
          
        if (ruleError) {
          console.error(`Error fetching rule ${rule.rule_id}:`, ruleError);
        }
        
        const currentResponse = ruleData?.response || "";
        
        // Get feedback samples for this rule to understand what users are complaining about
        // Correctly using a subquery with .in()
        const subquery = await supabase
          .from('assistant_rule_usage')
          .select('message_id')
          .eq('rule_id', rule.rule_id);
          
        const messageIds = subquery.data?.map(item => item.message_id) || [];
        
        const { data: feedbackSamples, error: feedbackError } = await supabase
          .from('assistant_feedback')
          .select('content_sample')
          .eq('feedback_type', 'negative')
          .in('message_id', messageIds)
          .limit(5);
          
        if (feedbackError) {
          console.error(`Error fetching feedback for rule ${rule.rule_id}:`, feedbackError);
        }
        
        const feedbackContent = feedbackSamples?.map(s => s.content_sample).filter(Boolean).join('\n') || '';
        
        // Generate improvement suggestion based on pattern, response and feedback
        const improvementSuggestion = await generateImprovement(
          rule.pattern,
          currentResponse,
          rule.negative_ratio,
          feedbackContent
        );
        
        return {
          rule_id: rule.rule_id,
          pattern: rule.pattern,
          current_response: currentResponse,
          negative_feedback_count: rule.negative_feedback_count,
          total_usage: rule.total_usage,
          negative_ratio: rule.negative_ratio,
          suggested_improvement: improvementSuggestion.suggestion,
          confidence_score: improvementSuggestion.confidence,
          status: 'pending'
        };
      })
    );
    
    // Save suggestions to database for admin review
    if (improvementSuggestions.length > 0) {
      await saveImprovementSuggestions(improvementSuggestions);
    }
    
    return improvementSuggestions;
  } catch (err) {
    console.error('Error in generateRuleImprovementSuggestions:', err);
    throw err;
  }
};

// Generate an improvement suggestion for a rule using ML/AI
const generateImprovement = async (
  pattern: string,
  currentResponse: string,
  negativeRatio: number,
  feedbackContent: string
): Promise<{ suggestion: string, confidence: number }> => {
  try {
    // Try to use AI for generating an improvement if available
    const { data: secretData } = await supabase.functions.invoke('get-secret', {
      body: { key: 'OPENAI_API_KEY' }
    });
    
    if (secretData?.value) {
      return await generateAIImprovement(pattern, currentResponse, negativeRatio, feedbackContent, secretData.value);
    }
  } catch (e) {
    console.error('Error getting OpenAI key or generating AI improvement:', e);
  }
  
  // Fallback to heuristic-based improvements
  return generateHeuristicImprovement(pattern, currentResponse, negativeRatio, feedbackContent);
};

// Generate improvement using AI
const generateAIImprovement = async (
  pattern: string,
  currentResponse: string,
  negativeRatio: number,
  feedbackContent: string,
  apiKey: string
): Promise<{ suggestion: string, confidence: number }> => {
  const openai = new OpenAI({
    apiKey: apiKey
  });
  
  try {
    const completion = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [
        {
          role: "system",
          content: "You are an assistant that helps improve AI rule responses based on negative user feedback. Your goal is to suggest improvements that address user concerns while maintaining the original purpose of the response."
        },
        {
          role: "user",
          content: `
Rule pattern: ${pattern}

Current response: 
${currentResponse}

This response has received negative feedback ${negativeRatio.toFixed(1)}% of the time.

${feedbackContent ? `Sample negative feedback:\n${feedbackContent}` : ''}

Please suggest an improved version of this response that addresses the issues in the negative feedback.
Only provide the improved response text, nothing else.`
        }
      ],
      temperature: 0.7,
      max_tokens: 1000
    });
    
    // Calculate confidence based on available feedback and negative ratio
    const confidence = calculateConfidenceScore(negativeRatio, !!feedbackContent);
    
    return { 
      suggestion: completion.choices[0]?.message?.content || currentResponse,
      confidence
    };
  } catch (e) {
    console.error('OpenAI API error:', e);
    // Fall back to heuristic method
    return generateHeuristicImprovement(pattern, currentResponse, negativeRatio, feedbackContent);
  }
};

// Calculate confidence score for the suggestion based on available data
const calculateConfidenceScore = (negativeRatio: number, hasFeedbackSamples: boolean): number => {
  let confidence = 0.5; // Base confidence
  
  // Adjust based on negative ratio severity
  if (negativeRatio > 80) {
    confidence += 0.2; // High negative ratio = higher confidence in need for change
  } else if (negativeRatio > 50) {
    confidence += 0.1;
  }
  
  // Adjust based on feedback sample availability
  if (hasFeedbackSamples) {
    confidence += 0.2; // Having specific feedback increases confidence
  }
  
  // Cap at 0.9 as we always want some room for human judgment
  return Math.min(confidence, 0.9);
};

// Generate an improvement suggestion for a rule using simple heuristics
const generateHeuristicImprovement = (
  pattern: string,
  currentResponse: string,
  negativeRatio: number,
  feedbackContent: string
): { suggestion: string, confidence: number } => {
  let suggestion = currentResponse;
  let confidence = 0;
  
  // Analyze feedback content for common issues if available
  const hasUnclearExplanation = feedbackContent.toLowerCase().includes('confus') || 
                               feedbackContent.toLowerCase().includes('unclear') ||
                               feedbackContent.toLowerCase().includes("don't understand");
                               
  const isTooLong = feedbackContent.toLowerCase().includes('long') || currentResponse.length > 500;
  const needsExamples = feedbackContent.toLowerCase().includes('example') || 
                       feedbackContent.toLowerCase().includes('show me');
                       
  // Apply heuristic improvements based on identified issues
  if (hasUnclearExplanation) {
    suggestion = simplifyResponse(currentResponse);
    confidence = 0.6;
  } else if (isTooLong) {
    suggestion = shortenResponse(currentResponse);
    confidence = 0.7;
  } else if (needsExamples) {
    suggestion = addExampleToResponse(currentResponse);
    confidence = 0.65;
  } else if (pattern.includes('?') && !currentResponse.includes('?')) {
    // If pattern is a question but response doesn't ask clarifying questions
    suggestion = `${currentResponse}\n\nIs there anything specific about this you'd like to know more about?`;
    confidence = 0.7;
  } else if (currentResponse.length > 500) {
    // If response is too long (might be overwhelming)
    suggestion = shortenResponse(currentResponse);
    confidence = 0.6;
  } else if (currentResponse.split('\n').length <= 2) {
    // If response lacks structure/formatting
    suggestion = currentResponse.replace('. ', '.\n\n');
    confidence = 0.5;
  } else {
    // Generic improvement - add a check-in
    suggestion = `${currentResponse}\n\nDoes this answer address your question?`;
    confidence = 0.4;
  }
  
  // If very high negative ratio, add low confidence flag
  if (negativeRatio > 80) {
    confidence = Math.min(confidence, 0.3); // This response needs human review
  }
  
  return { suggestion, confidence };
};

// Helper functions for heuristic improvements
const simplifyResponse = (text: string): string => {
  // Break into shorter paragraphs
  let simplified = text.replace(/\.\s+/g, '.\n\n');
  
  // Add a summary at the beginning
  simplified = `To summarize: ${text.split('.')[0]}.\n\n${simplified}`;
  
  return simplified;
};

const shortenResponse = (text: string): string => {
  const sentences = text.split(/[.!?]+/);
  
  // Keep first 3-4 sentences
  const shortened = sentences.slice(0, 4).join('. ') + '.';
  
  // Add a note that more details are available
  return `${shortened}\n\nWould you like more details on this topic?`;
};

const addExampleToResponse = (text: string): string => {
  // Add a generic example structure
  return `${text}\n\nFor example, if you were trying to [example scenario], you would [example solution].`;
};

// Save improvement suggestions to database
const saveImprovementSuggestions = async (suggestions: RuleImprovementSuggestion[]): Promise<void> => {
  try {
    // Convert to database format
    const dbSuggestions = suggestions.map(suggestion => ({
      rule_id: suggestion.rule_id,
      original_pattern: suggestion.pattern,
      original_response: suggestion.current_response,
      suggested_response: suggestion.suggested_improvement,
      negative_feedback_ratio: suggestion.negative_ratio,
      confidence_score: suggestion.confidence_score,
      status: suggestion.status,
      created_at: new Date().toISOString()
    }));
    
    // Insert into database
    const { error } = await supabase
      .from('rule_improvement_suggestions')
      .insert(dbSuggestions);
      
    if (error) {
      console.error('Error saving rule improvement suggestions:', error);
      throw error;
    }
    
  } catch (err) {
    console.error('Error in saveImprovementSuggestions:', err);
    throw err;
  }
};

// Get pending rule improvement suggestions for review
export const getPendingRuleSuggestions = async (): Promise<RuleImprovementSuggestion[]> => {
  try {
    const { data, error } = await supabase
      .from('rule_improvement_suggestions')
      .select(`
        id,
        rule_id,
        original_pattern,
        original_response,
        suggested_response,
        negative_feedback_ratio,
        confidence_score,
        status,
        created_at
      `)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error('Error fetching pending rule suggestions:', error);
      throw error;
    }
    
    // Convert to our interface format
    return data?.map(item => ({
      rule_id: item.rule_id,
      pattern: item.original_pattern,
      current_response: item.original_response,
      suggested_improvement: item.suggested_response,
      negative_feedback_count: 0, // Not stored in DB directly
      total_usage: 0, // Not stored in DB directly
      negative_ratio: item.negative_feedback_ratio,
      confidence_score: item.confidence_score,
      status: item.status as 'pending' | 'approved' | 'rejected' | 'implemented'
    })) || [];
    
  } catch (err) {
    console.error('Error in getPendingRuleSuggestions:', err);
    throw err;
  }
};

// Update a rule with the suggested improvement
export const implementRuleImprovement = async (
  suggestionId: string,
  approved: boolean
): Promise<{ success: boolean, error?: any }> => {
  try {
    if (approved) {
      // Get the suggestion details
      const { data: suggestion, error: fetchError } = await supabase
        .from('rule_improvement_suggestions')
        .select('rule_id, suggested_response')
        .eq('id', suggestionId)
        .single();
        
      if (fetchError) {
        console.error('Error fetching rule suggestion:', fetchError);
        return { success: false, error: fetchError };
      }
      
      // Update the rule with the new response
      const { error: updateError } = await supabase
        .from('assistant_rules')
        .update({ response: suggestion.suggested_response })
        .eq('id', suggestion.rule_id);
        
      if (updateError) {
        console.error('Error updating rule:', updateError);
        return { success: false, error: updateError };
      }
      
      // Update the suggestion status to implemented
      const { error: statusError } = await supabase
        .from('rule_improvement_suggestions')
        .update({ status: 'implemented' })
        .eq('id', suggestionId);
        
      if (statusError) {
        console.error('Error updating suggestion status:', statusError);
        return { success: false, error: statusError };
      }
      
      return { success: true };
    } else {
      // Just update status to rejected
      const { error } = await supabase
        .from('rule_improvement_suggestions')
        .update({ status: 'rejected' })
        .eq('id', suggestionId);
        
      if (error) {
        console.error('Error rejecting suggestion:', error);
        return { success: false, error };
      }
      
      return { success: true };
    }
  } catch (err) {
    console.error('Error in implementRuleImprovement:', err);
    return { success: false, error: err };
  }
};
